import React, { useContext, useRef } from 'react';
import PropTypes from 'prop-types';

import './SingleContentPage.scss';
import { Provider as CommunityContextProvider } from '../../../contexts/CommunityContext';
import { loadRightRailLayout } from '../../../utilities/loadPageContent';
import CommunityContentDisplay from '../../../components/Community/SingleContent/CommunityContentDisplay/CommunityContentDisplay';
import CommunityCommentsDisplay from '../../../components/Community/SingleContent/CommunityCommentsDisplay/CommunityCommentsDisplay';
import {
    singleTopicPropTypes,
    singleCommentPropTypes,
} from '../../defaultProps';
import Header from '../../../components/Groups/Header/Header';
import ThreeColumnLayout from '../../../components/Layout/ThreeColumnLayout/ThreeColumnLayout';
import LeftRailContent from '../../../components/LeftRailLayout/LeftRailContent/LeftRailContent';
import MobileModalTrigger from '../../../components/Groups/Modals/MobileModalTrigger/MobileModalTrigger';
import GroupsRightRail from '../../../components/Groups/GroupsRightRail/GroupsRightRail';
import Layout from '../../../components/Layout/Layout';
import { PageContext } from '../../../contexts/ContextCreator';
import RightRailContent from '../../../components/Layout/ThreeColumnGridLayout/RightRailContent/RightRailContent';
import { SingleContentPageConditionalWrapper } from './SingleContentPageConditionalComponent';
import MobileWandTrigger from '../../../components/CallToAction/MobileWandTrigger/MobileWandTrigger';
import LoginBanner from '../../../components/Login/LoginBanner/LoginBanner';
import {
    SnowplowPageViewTracker,
    PAGE_VIEW_SECTIONS,
    PAGE_VIEW_TYPES,
} from '../../../components/Analytics/Snowplow/SnowplowPageViewTracker';
import useStickyContent from '../../../hooks/useStickyContent';

export const PAGE_MODES = {
    COMMUNITY_CONTENT: 'community_content',
    COMMUNITY_COMMENT: 'community_comment',
};

const SingleContentPage = (props) => {
    const { modalTrigger } = useContext(PageContext);
    const { content, pageMode, ancestorHash, adminOnboardingProps } = props;

    const newAdminOnboardingProps = {
        ...adminOnboardingProps,
        showBanner: true,
    };

    const profileLeftRailCTAMetrics = {
        LAT: 'user-left-rail',
        LAC: 'user-left-rail',
        clickMetric: 'user-left-rail-profile-cta-click',
        impressionMetric: 'user-left-rail-profile-cta-impression',
    };

    const isGroupContent = !!content?.group;

    const ContentToShow = () => {
        if (pageMode === PAGE_MODES.COMMUNITY_COMMENT) {
            return (
                <CommunityCommentsDisplay
                    ancestorHash={ancestorHash}
                    content={content}
                />
            );
        }

        return (
            <CommunityContentDisplay
                content={content}
                hideReactionBar={props.hideReactionBar}
            />
        );
    };

    const leftRef = useRef(null);
    const [leftStyles] = useStickyContent(leftRef, false, 30);
    const rightRef = useRef(null);
    const [rightStyles] = useStickyContent(rightRef, false, 30);

    const Component = (
        <React.Fragment>
            <SnowplowPageViewTracker
                pageEntityId={`${content.type}-${content.hash}`}
                pageSection={
                    isGroupContent
                        ? PAGE_VIEW_SECTIONS.GROUPS
                        : PAGE_VIEW_SECTIONS.COMMUNITY
                }
                pageType={PAGE_VIEW_TYPES.CONTENT}
            />
            {isGroupContent ? (
                <div className="group-header-width-container">
                    <Header useGenericLink hideGroupsCTA />
                </div>
            ) : null}
            <ThreeColumnLayout>
                <ThreeColumnLayout.LeftRail
                    stickyContent={{
                        ref: leftRef,
                        styles: leftStyles,
                    }}
                >
                    <LeftRailContent
                        allowProfileCTA
                        profileLeftRailCTAMetrics={profileLeftRailCTAMetrics}
                    />
                </ThreeColumnLayout.LeftRail>
                <ThreeColumnLayout.MainContent>
                    <CommunityContextProvider
                        singleAttributionContentHash={props.content.hash}
                        attributionMode="single"
                        content={[props.content]}
                        topics={props.topics}
                    >
                        {ContentToShow()}
                    </CommunityContextProvider>
                    {isGroupContent ? (
                        <MobileModalTrigger
                            modalTrigger={(modalData) =>
                                modalTrigger('GroupsMobileModal', {
                                    ...modalData,
                                    isReactRouterLink: false,
                                    onClose: () => modalTrigger(),
                                })
                            }
                        />
                    ) : (
                        <MobileWandTrigger location="single-content" />
                    )}
                </ThreeColumnLayout.MainContent>
                <ThreeColumnLayout.RightRail
                    stickyContent={{
                        ref: rightRef,
                        styles: rightStyles,
                    }}
                >
                    {isGroupContent ? (
                        <GroupsRightRail location={props.location} />
                    ) : (
                        <RightRailContent
                            contents={loadRightRailLayout('EditorialContent')}
                        />
                    )}
                </ThreeColumnLayout.RightRail>
            </ThreeColumnLayout>
        </React.Fragment>
    );

    return (
        <div className="page-Community-SingleContentPage">
            <Layout
                {...props}
                displayDesktopFooter
                topBannerOnlyShownList={['admin-onboard']}
                adminOnboardingProps={newAdminOnboardingProps}
                hasChangingContainer={false}
                fullScreenStyling
                hasBottomBanner={false}
                className={props.group?.groupUrl ? 'groups-layout' : ''}
            >
                <SingleContentPageConditionalWrapper
                    PAGE_PROPS={props.PAGE_PROPS}
                    userRelation={props.userRelation}
                    group={props.group}
                    render={() => Component}
                />
                <LoginBanner />
            </Layout>
        </div>
    );
};

// @TODO update this later with contract
SingleContentPage.propTypes = {
    PAGE_PROPS: PropTypes.shape({ featureFlags: {} }),
    /* eslint-disable-next-line  react/no-unused-prop-types */
    pageMode: PropTypes.oneOf([
        PAGE_MODES.COMMUNITY_CONTENT,
        PAGE_MODES.COMMUNITY_COMMENT,
    ]),
    /**
     * This is used in Three Column Grid Layout
     */
    topics: PropTypes.arrayOf(singleTopicPropTypes).isRequired,
    /**
     * This is used in Three Column Grid Layout
     */
    currentTopic: singleTopicPropTypes.isRequired,
    adminOnboardingProps: PropTypes.shape({}),
    /**
     * Content props
     */
    content: PropTypes.shape({
        group: PropTypes.string,
        type: PropTypes.string,
        hash: PropTypes.string,
        lastId: PropTypes.number,
        ancestorHash: PropTypes.string,
        commentCount: PropTypes.number,
        comments: PropTypes.arrayOf(PropTypes.shape(singleCommentPropTypes)),
    }).isRequired,
    ancestorHash: PropTypes.string,
    /**
     * Boolean as to hide reaction bar for the Editorial/Podcast Content
     */
    // This is a known es-lint bug where if it's a prop that's just passed, it's not considered used...
    /* eslint-disable-next-line  react/no-unused-prop-types */
    hideReactionBar: PropTypes.bool,
    group: PropTypes.shape({
        groupUrl: PropTypes.string,
    }),
    location: PropTypes.string,
    userRelation: PropTypes.shape({}),
};

SingleContentPage.defaultProps = {
    PAGE_PROPS: {},
    adminOnboardingProps: {},
    pageMode: PAGE_MODES.COMMUNITY_CONTENT,
    hideReactionBar: false,
    group: null,
    location: null,
    userRelation: null,
    ancestorHash: null,
};

export default React.memo(SingleContentPage);
